import { VyVConsentPostRequest } from 'src/app/api/models/vyVConsentPostRequest';
import { VyVContractCheckIBANRequest } from 'src/app/api/models/vyVContractCheckIBANRequest';
import { VyVContractCreateRequest } from 'src/app/api/models/vyVContractCreateRequest';
import { VyVContractEnvoiAttestationBailleurInfo } from 'src/app/api/models/vyVContractEnvoiAttestationBailleurInfo';
import { VyVContractResiliationAncienneAssuranceInfo } from 'src/app/api/models/vyVContractResiliationAncienneAssuranceInfo';
import { VyVContractSaisiePaiement } from 'src/app/api/models/vyVContractSaisiePaiement';
import { VyVDevisCreateRequest } from 'src/app/api/models/vyVDevisCreateRequest';
import { VyVSharedCodeFormule } from 'src/app/api/models/vyVSharedCodeFormule';
import { VyVSharedProductCode } from 'src/app/api/models/vyVSharedProductCode';
import { VyVSignatureCreateRequest } from 'src/app/api/models/vyVSignatureCreateRequest';
import { VyVSignatureSignRequest } from 'src/app/api/models/vyVSignatureSignRequest';
import { VyVTarifCreateRequest } from 'src/app/api/models/vyVTarifCreateRequest';
import { VyVTarifEnfantInfo } from 'src/app/api/models/vyVTarifEnfantInfo';
import { VyVTarifSaisieAdresse } from 'src/app/api/models/vyVTarifSaisieAdresse';
import { VyVTarifSaisieHabitation } from 'src/app/api/models/vyVTarifSaisieHabitation';
import { VyVTarifSaisiePersonnalisation } from 'src/app/api/models/vyVTarifSaisiePersonnalisation';
import { PathType } from 'src/app/core/enums/path-type.enum';
import {
  EcheancePrelevement,
  Esh,
  Formule,
  MontantValeurObjets2000,
  NatureResidence,
  QualiteOccupant,
  Raison,
  SaisieBeneficiaire,
  SaisieChoixServices,
  SaisieFormule,
  SaisieHabitation,
  SaisieInfosSouscripteur,
  SaisiePaiement,
  SaisiePersonnalisation,
  SaisieSignature,
  Selection,
  Selectionne,
  SurfaceDependance,
  TypeBailleur,
  TypeHabitation,
  VoiesConsentement
} from 'src/app/core/models/form-state.model';

export class ApiRequestBodyUtils {
  static GenerateProductData(product: PathType, brand: string): VyVSharedProductCode {
    switch (product) {
      case PathType.HOUSING:
        return VyVSharedProductCode.HC;
      case PathType.HOUSING_PARIS:
        return VyVSharedProductCode.HP;
      case PathType.HOUSING_SOLIDARITY:
        return VyVSharedProductCode.HS;
      case PathType.HOUSING_BAILLEURS_SOCIAUX:
        return VyVSharedProductCode.BS;
      case PathType.HOUSING_TOURCOING:
        return VyVSharedProductCode.HT;
      case PathType.HOUSING_LILLE:
        return VyVSharedProductCode.HL;
      case PathType.HOUSING_GRENOBLE:
        return VyVSharedProductCode.HG;
      case PathType.HOUSING_CRETEIL:
        return VyVSharedProductCode.HR;
      case PathType.HOUSING_MARSEILLE:
        return VyVSharedProductCode.HA;
      case PathType.HOUSING_ETUDIANTE:
        return VyVSharedProductCode.HE;
      default:
        return brand === 'HM' ? VyVSharedProductCode.HM_HC : VyVSharedProductCode.HC;
    }
  }

  static GenerateSaisieAdresseData(
    adresseType: Selectionne,
    adresseSelection: Selection | undefined,
    adresseManuelle: Esh | undefined,
    adresseEsh: Esh | undefined
  ): VyVTarifSaisieAdresse {
    switch (adresseType) {
      case Selectionne.Selection: {
        return {
          AdresseComplete: adresseSelection?.Adresse,
          NumeroDeRue: adresseSelection?.AdresseEsh.Numero!,
          NomDeRue: adresseSelection?.AdresseEsh.Rue!,
          CodePostal: adresseSelection?.AdresseEsh.CodePostal!,
          Ville: adresseSelection?.AdresseEsh.Ville!,
          CodePays: adresseSelection?.AdresseEsh.Pays!
        } as VyVTarifSaisieAdresse;
      }
      case Selectionne.Manuel: {
        return {
          NumeroEtRue: adresseManuelle?.NumeroEtRue!,
          CodePostal: adresseManuelle?.CodePostal!,
          Ville:
            typeof adresseManuelle?.Ville === 'string'
              ? adresseManuelle?.Ville
              : adresseManuelle?.Ville?.nomCommune,
          Pays: adresseManuelle?.Pays!
        } as VyVTarifSaisieAdresse;
      }
      case Selectionne.Esh: {
        return {
          NomBailleur: adresseEsh?.NomBailleur!,
          NumeroEtRue: adresseEsh?.NumeroEtRue!,
          CodePostal: adresseEsh?.CodePostal!,
          Ville: adresseEsh?.Ville!,
          Pays: adresseEsh?.Pays!
        } as VyVTarifSaisieAdresse;
      }
      default: {
        return {} as VyVTarifSaisieAdresse;
      }
    }
  }

  static GenerateSaisieHabitationData(
    saisieHabitation: SaisieHabitation
  ): VyVTarifSaisieHabitation {
    return {
      TypeHabitation:
        saisieHabitation.TypeHabitation == TypeHabitation.Appartement
          ? VyVTarifSaisieHabitation.TypeHabitationEnum.Appartement
          : saisieHabitation.TypeHabitation == TypeHabitation.Maison
          ? VyVTarifSaisieHabitation.TypeHabitationEnum.Maison
          : saisieHabitation.TypeHabitation == TypeHabitation.Chambre
          ? VyVTarifSaisieHabitation.TypeHabitationEnum.Chambre
          : VyVTarifSaisieHabitation.TypeHabitationEnum.Autre,
      QualiteOccupant:
        saisieHabitation.QualiteOccupant == QualiteOccupant.Locataire
          ? VyVTarifSaisieHabitation.QualiteOccupantEnum.Locataire
          : VyVTarifSaisieHabitation.QualiteOccupantEnum.Proprietaire,
      NatureResidence:
        saisieHabitation.NatureResidence == NatureResidence.Secondaire
          ? VyVTarifSaisieHabitation.NatureResidenceEnum.Secondaire
          : VyVTarifSaisieHabitation.NatureResidenceEnum.Principale,
      TarifPreferentiel:
        saisieHabitation.TarifPreferentiel !== undefined
          ? saisieHabitation.TarifPreferentiel
          : false,
      CompositionFoyer:
        saisieHabitation.CompositionFoyer !== undefined ? saisieHabitation.CompositionFoyer : null,
      RFRInferieurPlafond:
        saisieHabitation.RFRInferieurPlafond !== undefined
          ? saisieHabitation.RFRInferieurPlafond
          : null,
      ConfirmationRFRInferieurPlafond:
        saisieHabitation.ConfirmationRFRInferieurPlafond !== undefined
          ? saisieHabitation.ConfirmationRFRInferieurPlafond
          : null,
      NombrePieces: saisieHabitation.NombrePieces!,
      NombrePiecesSuperieures:
        saisieHabitation.NombrePiecesSuperieures !== undefined
          ? saisieHabitation.NombrePiecesSuperieures
          : null,
      Etage:
        (saisieHabitation.Etage as VyVTarifSaisieHabitation.EtageEnum) ??
        VyVTarifSaisieHabitation.EtageEnum.None,
      AvecVeranda: saisieHabitation.AvecVeranda !== undefined ? saisieHabitation.AvecVeranda : null,
      AntecedentSinistresInondables:
        saisieHabitation.AntecedentSinistresInondables !== undefined
          ? saisieHabitation.AntecedentSinistresInondables
          : null,
      SurfaceDependance:
        saisieHabitation.SurfaceDependance == SurfaceDependance.PlusDe100m2
          ? VyVTarifSaisieHabitation.SurfaceDependanceEnum.PlusDe100m2
          : saisieHabitation.SurfaceDependance == SurfaceDependance.De50a100m2
          ? VyVTarifSaisieHabitation.SurfaceDependanceEnum.De50a100m2
          : saisieHabitation.SurfaceDependance == SurfaceDependance.MoinsDe50m2
          ? VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe50m2
          : saisieHabitation.SurfaceDependance == SurfaceDependance.MoinsDe40m2
          ? VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe40m2
          : saisieHabitation.SurfaceDependance ==
            VyVTarifSaisieHabitation.SurfaceDependanceEnum.Aucune
          ? VyVTarifSaisieHabitation.SurfaceDependanceEnum.Aucune
          : null,
      InstallationDD:
        saisieHabitation.InstallationDD !== undefined ? saisieHabitation.InstallationDD : null,
      PiscineSpaJacuzzi:
        saisieHabitation.PiscineSpaJacuzzi !== undefined
          ? saisieHabitation.PiscineSpaJacuzzi
          : null,
      ObjetsValeurSuperieur2000:
        saisieHabitation.ObjetsValeurSuperieur2000 !== undefined
          ? saisieHabitation.ObjetsValeurSuperieur2000
          : null,
      PlafondMobilier:
        saisieHabitation.PlafondMobilier == Formule.F2
          ? VyVTarifSaisieHabitation.PlafondMobilierEnum.F2
          : VyVTarifSaisieHabitation.PlafondMobilierEnum.F1,
      TypeChambre:
        saisieHabitation.SuperficieChambre !== undefined
          ? String(saisieHabitation.SuperficieChambre)
          : null,
      MontantPlafondMobilier: saisieHabitation.MontantRFR ? saisieHabitation.MontantRFR : null,
      NombreColocataires: saisieHabitation.NombreColocataires ?? null
    };
  }

  static GenerateTarifCreateRequestBody(
    context: string,
    product: PathType,
    brand: string,
    adresseType: Selectionne,
    adresseSelection: Selection | undefined,
    adresseManuelle: Esh | undefined,
    adresseEsh: Esh | undefined,
    saisieHabitation: SaisieHabitation,
    saisieFormule: SaisieFormule | undefined,
    formuleRecommandee: Formule,
    coverageDurationInMonths: number | null,
    email: string,
    lessorName: string | null
  ): VyVTarifCreateRequest {
    const body: VyVTarifCreateRequest = {
      Context: context,
      SaisieAdresse: ApiRequestBodyUtils.GenerateSaisieAdresseData(
        adresseType,
        adresseSelection,
        adresseManuelle,
        adresseEsh
      ),
      SaisieHabitation: ApiRequestBodyUtils.GenerateSaisieHabitationData(saisieHabitation),
      SaisieProduit: {
        IdProduit: ApiRequestBodyUtils.GenerateProductData(product, brand),
        FormuleSelectionnee:
          saisieFormule !== undefined
            ? ApiRequestBodyUtils.ConvertCodeFormule(saisieFormule.FormuleSelectionnee)
            : null,
        FormuleRecommandee: ApiRequestBodyUtils.ConvertCodeFormule(formuleRecommandee),
        CodePromo: saisieFormule !== undefined ? saisieFormule.CodePromo || null : null,
        SponsorEmail: saisieFormule !== undefined ? saisieFormule.SponsorEmail || null : null
      },
      SaisiePersonnalisation: null,
      CoverageDurationInMonths: coverageDurationInMonths,
      Email: email
    };

    if (lessorName) {
      Object.assign(body, { LessorName: lessorName });
    }
    return body;
  }

  static ConvertCodeFormule(codeFormule: Formule | undefined): VyVSharedCodeFormule | null {
    switch (codeFormule) {
      case Formule.F1:
        return VyVSharedCodeFormule.F1;
      case Formule.F2:
        return VyVSharedCodeFormule.F2;
      case Formule.LM:
        return VyVSharedCodeFormule.LM;
      default:
        return null;
    }
  }

  static GenerateTarifCreateRequestBodyWithPersonnalisation(
    context: string,
    product: PathType,
    brand: string,
    adresseType: Selectionne,
    adresseSelection: Selection | undefined,
    adresseManuelle: Esh | undefined,
    adresseEsh: Esh | undefined,
    saisieHabitation: SaisieHabitation,
    saisieFormule: SaisieFormule,
    formuleRecommandee: Formule,
    saisiePersonnalisation: SaisiePersonnalisation,
    coverageDurationInMonths: number | null,
    email: string
  ): VyVTarifCreateRequest {
    let body = ApiRequestBodyUtils.GenerateTarifCreateRequestBody(
      context,
      product,
      brand,
      adresseType,
      adresseSelection,
      adresseManuelle,
      adresseEsh,
      saisieHabitation,
      saisieFormule,
      formuleRecommandee,
      coverageDurationInMonths,
      email,
      null
    );

    // Insert personnalisation data
    body.SaisiePersonnalisation = {
      ProtectionJuridique: saisiePersonnalisation.ProtectionJuridique,
      ProtectionNomades: saisiePersonnalisation.ProtectionNomades,
      ProtectionVelos: saisiePersonnalisation.ProtectionVelos,
      MontantValeurObjets2000:
        saisiePersonnalisation.MontantValeurObjets2000 == MontantValeurObjets2000.Montant20000
          ? VyVTarifSaisiePersonnalisation.MontantValeurObjets2000Enum.Montant15000
          : saisiePersonnalisation.MontantValeurObjets2000 == MontantValeurObjets2000.Montant10000
          ? VyVTarifSaisiePersonnalisation.MontantValeurObjets2000Enum.Montant10000
          : VyVTarifSaisiePersonnalisation.MontantValeurObjets2000Enum.Montant5000,
      AssuranceScolaire:
        saisiePersonnalisation.AssuranceScolaire == null
          ? []
          : saisiePersonnalisation.AssuranceScolaire.map(
              a =>
                ({
                  Numero: a.Numero,
                  Nom: a.Nom,
                  Prenom: a.Prenom,
                  DateNaissance: a.DateNaissance
                } as VyVTarifEnfantInfo)
            ),
      ValeurANeuf: saisiePersonnalisation.ValeurANeuf,
      Vol: saisiePersonnalisation.Vol
    };

    return body;
  }

  static GenerateDevisCreateRequestBody(
    context: string,
    clientAdvisorFullName: string,
    product: PathType,
    brand: string,
    adresseType: Selectionne,
    adresseSelection: Selection | undefined,
    adresseManuelle: Esh | undefined,
    adresseEsh: Esh | undefined,
    saisieHabitation: SaisieHabitation,
    souscripteurInfos: SaisieInfosSouscripteur,
    saisieFormule: SaisieFormule,
    formuleRecommandee: Formule,
    assureurInfos: SaisieInfosSouscripteur | undefined,
    coverageDurationInMonths: number | undefined,
    partnerCode: string | undefined,
    lessorName: string | null
  ): VyVDevisCreateRequest {
    if (saisieFormule.FormuleSelectionnee === undefined) {
      throw new Error('saisieFormule.FormuleSelectionnee not set');
    }
    let obj: VyVDevisCreateRequest = {
      Context: context,
      ClientAdvisorFullName: clientAdvisorFullName,
      SaisieHabitation: ApiRequestBodyUtils.GenerateSaisieHabitationData(saisieHabitation),
      SaisieFormule: {
        IdProduit: ApiRequestBodyUtils.GenerateProductData(product, brand),
        FormuleSelectionnee: ApiRequestBodyUtils.ConvertCodeFormule(
          saisieFormule.FormuleSelectionnee
        )!,
        FormuleRecommandee: ApiRequestBodyUtils.ConvertCodeFormule(formuleRecommandee)!
      },
      SaisieInfosSouscripteur: {
        Adresse: ApiRequestBodyUtils.GenerateSaisieAdresseData(
          adresseType,
          adresseSelection,
          adresseManuelle,
          adresseEsh
        ),
        Civilite: souscripteurInfos?.Civilite,
        Nom: souscripteurInfos?.Nom,
        Prenom: souscripteurInfos?.Prenom,
        DateNaissance: souscripteurInfos?.DateNaissance,
        VilleNaissance: souscripteurInfos?.LieuNaissance,
        PaysNaissance: souscripteurInfos?.PaysNaissance,
        CodePostaleNaissance: souscripteurInfos?.CodePostalNaissance,
        Nationalite: souscripteurInfos?.Nationalite,
        Email: souscripteurInfos?.Email,
        Telephone: souscripteurInfos?.Indicatif + '-' + souscripteurInfos?.Telephone
      },
      SaisieInfoAssure: null,
      CoverageDurationInMonths: coverageDurationInMonths,
      PartnerCode: partnerCode,
      LessorName: lessorName
    };

    if (souscripteurInfos?.ValeurAssuree)
      obj.SaisieInfosSouscripteur.ValeurAssuree = souscripteurInfos?.ValeurAssuree;

    if (assureurInfos) {
      obj.SaisieInfoAssure = {
        Adresse: ApiRequestBodyUtils.GenerateSaisieAdresseData(
          Selectionne.Undefined,
          undefined,
          undefined,
          undefined
        ),
        Civilite: assureurInfos.Civilite,
        Nom: assureurInfos.Nom,
        Prenom: assureurInfos.Prenom,
        DateNaissance: assureurInfos.DateNaissance,
        VilleNaissance: assureurInfos.LieuNaissance,
        PaysNaissance: assureurInfos.PaysNaissance,
        CodePostaleNaissance: assureurInfos.CodePostalNaissance,
        Nationalite: assureurInfos.Nationalite,
        Email: assureurInfos.Email,
        Telephone: assureurInfos.Indicatif + '-' + assureurInfos.Telephone
      };
    }
    return obj;
  }

  static GenerateCheckIbanRequestBody(context: string, iban: string): VyVContractCheckIBANRequest {
    return {
      Context: context,
      IBAN: iban
    };
  }

  static GenerateContractCreateRequestBody(
    context: string,
    devisId: string,
    beneficiaire: SaisieBeneficiaire | null,
    saisieChoixServices: SaisieChoixServices,
    saisiePaiement: SaisiePaiement
  ): VyVContractCreateRequest {
    return {
      Context: context,
      DevisId: devisId,
      SaisieBeneficiaire:
        beneficiaire !== null
          ? {
              BeneficiaireTiers: {
                Civilite: beneficiaire.Civilite,
                Nom: beneficiaire.Nom,
                Prenom: beneficiaire.Prenom,
                Email: beneficiaire.Email,
                Telephone: beneficiaire.Telephone
              }
            }
          : null,
      SaisieChoixServices: {
        EnvoiAttestationBailleur:
          saisieChoixServices.EnvoiAttestationBailleur !== undefined && saisieChoixServices.EnvAttB
            ? {
                TypeBailleur:
                  saisieChoixServices.EnvoiAttestationBailleur.TypeBailleur == TypeBailleur.Prive
                    ? VyVContractEnvoiAttestationBailleurInfo.TypeBailleurEnum.Prive
                    : VyVContractEnvoiAttestationBailleurInfo.TypeBailleurEnum.Social,
                BailleurPrive:
                  saisieChoixServices.EnvoiAttestationBailleur.TypeBailleur == TypeBailleur.Prive
                    ? {
                        NomBailleur: null,
                        Email: saisieChoixServices.EnvoiAttestationBailleur.BailleurPrive!.Email
                      }
                    : null,
                BailleurSocial:
                  saisieChoixServices.EnvoiAttestationBailleur.TypeBailleur == TypeBailleur.Social
                    ? {
                        NomBailleur:
                          saisieChoixServices.EnvoiAttestationBailleur.BailleurSocial!.NomBailleur,
                        Email: saisieChoixServices.EnvoiAttestationBailleur.BailleurSocial!.Email,
                        Reference1:
                          saisieChoixServices.EnvoiAttestationBailleur.BailleurSocial!.Reference1,
                        Reference2:
                          saisieChoixServices.EnvoiAttestationBailleur.BailleurSocial!.Reference2,
                        LogementOccupe:
                          saisieChoixServices.EnvoiAttestationBailleur.BailleurSocial
                            ?.LogementOccupe ?? null
                      }
                    : null
              }
            : null,
        ResiliationAncienneAssurance:
          saisieChoixServices.ResiliationAncienneAssurance !== undefined &&
          saisieChoixServices.ResAncAss
            ? {
                Raison:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison === Raison.Emmenagement
                    ? VyVContractResiliationAncienneAssuranceInfo.RaisonEnum.Emmenagement
                    : VyVContractResiliationAncienneAssuranceInfo.RaisonEnum.ChangementAssureur,
                NomAssureur:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison ===
                  Raison.ChangementAssureur
                    ? saisieChoixServices.ResiliationAncienneAssurance.NomAssureur
                    : null,
                AdresseAssureur:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison ===
                  Raison.ChangementAssureur
                    ? {
                        CodePostal:
                          saisieChoixServices.ResiliationAncienneAssurance.CodePostalAssureur,
                        Ville: saisieChoixServices.ResiliationAncienneAssurance.VilleAssureur,
                        NumeroEtRue:
                          saisieChoixServices.ResiliationAncienneAssurance.AddressAssureur
                      }
                    : null,
                NumeroContrat:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison ===
                  Raison.ChangementAssureur
                    ? saisieChoixServices.ResiliationAncienneAssurance.NumeroContrat
                    : null,
                MoisDemarrageContrat:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison ===
                  Raison.ChangementAssureur
                    ? saisieChoixServices.ResiliationAncienneAssurance.MoisDemarrageContrat
                    : null,
                DateEnvoiLR:
                  saisieChoixServices.ResiliationAncienneAssurance.Raison ===
                  Raison.ChangementAssureur
                    ? saisieChoixServices.ResiliationAncienneAssurance.DateEnvoiLR
                    : null
              }
            : null,
        OppositionMarketing: saisieChoixServices.OppositionMarketing,
        NombreEnfants: saisieChoixServices.NombreEnfants
      },
      SaisiePaiement: {
        EcheancePrelevement:
          saisiePaiement.EcheancePrelevement == EcheancePrelevement.Trimestriel
            ? VyVContractSaisiePaiement.EcheancePrelevementEnum.Trimestriel
            : saisiePaiement.EcheancePrelevement == EcheancePrelevement.Annuel
            ? VyVContractSaisiePaiement.EcheancePrelevementEnum.Annuel
            : VyVContractSaisiePaiement.EcheancePrelevementEnum.Mensuel,
        DateDemarrage: saisiePaiement.DateDemarrage!,
        JourPrelevement:
          saisiePaiement.JourPrelevement === '5'
            ? VyVContractSaisiePaiement.JourPrelevementEnum.Le5DuMois
            : saisiePaiement.JourPrelevement === '10'
            ? VyVContractSaisiePaiement.JourPrelevementEnum.Le10DuMois
            : saisiePaiement.JourPrelevement === '15'
            ? VyVContractSaisiePaiement.JourPrelevementEnum.Le15DuMois
            : VyVContractSaisiePaiement.JourPrelevementEnum.Le5DuMois,
        IBAN: saisiePaiement.IBAN,
        Telephone: saisiePaiement.Telephone,
        TitulaireCompte: saisiePaiement.TitulaireCompte!
      }
    };
  }

  static GenerateSignatureCreateRequestBody(
    context: string,
    contractId: string,
    saisieSignature: SaisieSignature
  ): VyVSignatureCreateRequest {
    return {
      Context: context,
      ContractId: contractId,
      AcceptationCP: saisieSignature.AcceptationCP,
      AcceptationFIC: saisieSignature.AcceptationFIC,
      AcceptationMandatSEPA: saisieSignature.AcceptationMandatSEPA
    };
  }

  static GenerateSignatureSignRequestBody(
    context: string,
    contractId: string,
    saisieSignature: SaisieSignature
  ): VyVSignatureSignRequest {
    return {
      Context: context,
      ContractId: contractId,
      TokenSignature: saisieSignature.SignatureToken!,
      CodeSMS: saisieSignature.CodeSms!
    };
  }

  static GeneratePostConsentRequestBody(
    context: string,
    idLibelle: number,
    Voies: VoiesConsentement[]
  ): VyVConsentPostRequest {
    return {
      sessionId: context,
      idLibelle: idLibelle,
      voies: Voies.map(v => ({
        id: v.Id,
        isAccepted: v.IsCheck ?? false
      }))
    };
  }
}
